"use client"

import styled from "@emotion/styled"
import { Box, IconButton, Tooltip } from "@mui/material"
import dynamic from "next/dynamic"
import { useSearchParams } from "next/navigation"
import { RefObject, useEffect, useState } from "react"
import { useInView } from "react-intersection-observer"
import { copyColorToClipboard, scrollToCardById, shareColorNavigator } from "utils"
import * as animationData from "../LottieAnimations/Bg-Bond-Loading.json"
import NewText from "../NewText/NewText"
import { useIsDesktop } from "../common/bit/MediaQueries"
import { ShareIcon } from "../icons"

// Dynamically import Lottie to disable SSR for the animation component
const Lottie = dynamic(() => import("lottie-react"), { ssr: false })

const StyledColorPalleteWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  justify-content: space-between;
  gap: 24px;
  ${(props) => props.theme.breakpoints.up("md")} {
    row-gap: 40px;
    column-gap: 16px;
    grid-template-columns: repeat(7, minmax(0, 170px));
  }
`
const StyledColorCard = styled.div`
  width: 100%;
  height: 159;
  gap: 16px;
  border-radius: 16px;
  padding: 16px;
  background: white;
  box-shadow: 0px 4px 12px 0px #00000029;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const StyledColorCardShade = styled.div<{ colorCode: string }>`
  background: ${(props) => props.colorCode};
  width: 100%;
  height: 82px;
  border-radius: 8px;
`

const StyledLoadingWrapper = styled.div`
  margin: 0 auto;
  margin-top: 24px;
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  ${(props) => props.theme.breakpoints.up("md")} {
    margin-top: 40px;
  }
`

type Props = {
  data: {
    colorCode: string
    colorNumber: string
    colorCodeForScroller: string
  }[]
  inView: boolean
  isManuallyDragging: boolean
  gradientColorsForScroller: string[]
  scrollerRef: RefObject<HTMLDivElement>
  // eslint-disable-next-line no-unused-vars
  debounceChangeAnchorState: (color: string, position: number) => void
  displayMoreCards: () => void
  sliceEndNumber: number
  cardRefs: RefObject<Record<string, HTMLDivElement | null>>
  sharingText: string
}

export default function ColorCards({
  data,
  inView,
  isManuallyDragging,
  gradientColorsForScroller,
  scrollerRef,
  debounceChangeAnchorState,
  displayMoreCards,
  sliceEndNumber,
  cardRefs,
  sharingText,
}: Props) {
  const isDesktop = useIsDesktop()

  const defaultOptions = {
    animationData: animationData,
    loop: true,
  }
  const { ref: loadingRef, inView: loadingInView } = useInView({
    triggerOnce: false,
    threshold: 1,
  })
  //   const cardRefs = useRef<Record<string, HTMLDivElement | null>>({})

  useEffect(() => {
    setTimeout(() => {
      loadingInView && displayMoreCards()
    }, 500)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingInView])

  // scroll down to card from url param
  const searchParams = useSearchParams()
  useEffect(() => {
    searchParams?.get("colorCode") &&
      inView &&
      scrollToCardById(searchParams?.get("colorCode") || "", data, sliceEndNumber, displayMoreCards)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView, searchParams])

  useEffect(() => {
    if (isManuallyDragging) return
    const handleScroll = () => {
      const centerY = window.innerHeight / 2

      let closestCard: { id: string; distance: number } | null = null

      cardRefs.current &&
        Object.entries(cardRefs.current).forEach(([id, el]) => {
          if (el) {
            const rect = el.getBoundingClientRect()
            const cardCenterY = rect.top + rect.height / 2
            const distance = Math.abs(centerY - cardCenterY)

            if (!closestCard || distance < closestCard.distance) {
              closestCard = { id, distance }
            }
          }
        })
      //@ts-ignore
      if (closestCard && closestCard.id) {
        //@ts-ignore
        const colorId = closestCard.id
        const colorIndex = gradientColorsForScroller?.findIndex((color) => color === colorId)
        const positionForAnchor = colorIndex / (gradientColorsForScroller?.length - 1) || 0
        if (!scrollerRef.current) return
        const rect = scrollerRef.current.getBoundingClientRect()
        const newLeft = rect.width * positionForAnchor
        debounceChangeAnchorState(colorId, newLeft)
      }
    }

    !isManuallyDragging && window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isManuallyDragging])

  const [tooltipOpen, setTooltipOpen] = useState<string | boolean>(false)

  return (
    <>
      <StyledColorPalleteWrapper>
        {data
          ?.slice(0, sliceEndNumber)
          ?.map(
            (
              color: { colorCode: string; colorNumber: string; colorCodeForScroller: string },
              i: number,
            ) => {
              const currentRef = cardRefs.current
              if (currentRef && !currentRef[color.colorCodeForScroller]) {
                currentRef[color.colorCodeForScroller] = null
              }
              return (
                <StyledColorCard
                  key={i}
                  id={color.colorCode}
                  data-color-code-for-scroller={color.colorCodeForScroller}
                  ref={(el) => {
                    if (el && currentRef && !currentRef[color.colorCodeForScroller]) {
                      currentRef[color.colorCodeForScroller] = el
                    }
                  }}
                >
                  <StyledColorCardShade colorCode={color.colorNumber} />
                  <Box display="flex" alignItems="center" justifyContent={"space-between"}>
                    <NewText
                      desktopFontSize={20}
                      desktopFontWeight="600"
                      desktopLineHeight="100%"
                      mobileFontSize={18}
                      mobileFontWeight="600"
                      mobileLineHeight="100%"
                      component="h1"
                      color={"black"}
                    >
                      {color.colorCode}
                    </NewText>
                    <Tooltip
                      title={"הצבע הועתק, כעת ניתן להדביק אותו היכן שתבחרו"}
                      open={tooltipOpen === color.colorCode}
                      placement="top"
                      disableHoverListener
                    >
                      <IconButton
                        style={{
                          cursor: "pointer",
                        }}
                        onClick={() =>
                          isDesktop
                            ? copyColorToClipboard(color.colorCode, setTooltipOpen)
                            : shareColorNavigator(color.colorCode, setTooltipOpen, sharingText)
                        }
                      >
                        <ShareIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </StyledColorCard>
              )
            },
          )}
      </StyledColorPalleteWrapper>
      {sliceEndNumber < data?.length && (
        <StyledLoadingWrapper id={"loading"} ref={loadingRef} onClick={displayMoreCards}>
          <Lottie
            // @ts-ignore dynamic import typing
            animationData={defaultOptions.animationData}
            loop={defaultOptions.loop}
            style={{ width: 56, height: 56 }}
          />
        </StyledLoadingWrapper>
      )}
    </>
  )
}
